// DRAFT copy (TECH-197): built as the ticket writes it; Stuart approves before POLLS_ENABLED goes on.
// Every string the poll card renders. On screen it is "answer", never the other word, with the two
// exceptions the signed-off frames carry (TECH-197 § Voting); pollCopy.test.ts enforces it.

/** " · {count}" after an option on a "Who answered" chip (B6). */
const POLL_CHIP_COUNT = (count: number) => ` · ${count}`;

/**
 * DRAFT. A post that is not there, on the post page and the Post sheet only (founder, 3 Nov). Every other screen,
 * the edit form among them, keeps the shared line from `buildLoadErrorCopy`.
 */
export const POST_NOT_FOUND_COPY = {
  title: 'This post has left the room.',
  description: 'It may have been removed, or the link lost a few letters on the way here.',
};

export const POLL_COPY = {
  // "Who can create polls" (C1, the Space setup screen). The name is approved (PR #626).
  whoCanCreatePolls: 'Who can create polls',
  pollCreatorsAdmins: 'Admins and moderators',
  pollCreatorsAdminsHelper: 'Only you and your team can create polls',
  pollCreatorsEveryone: 'All members',
  pollCreatorsEveryoneHelper: 'Any member of this Space can create a poll',

  pickOne: 'Pick one',
  pickOneOrMore: 'Pick one or more',
  anonymousPoll: 'Anonymous poll',
  finalResults: 'Final results',

  noEndTime: 'No end time',
  endsInDays: (days: number) => (days === 1 ? 'Ends in 1 day' : `Ends in ${days} days`),
  /** "Ends in 1 h 52 min"; a whole hour "Ends in 2 h". */
  endsInHoursMinutes: (hours: number, minutes: number) =>
    minutes === 0 ? `Ends in ${hours} h` : `Ends in ${hours} h ${minutes} min`,
  endsInMinutes: (minutes: number) => `Ends in ${minutes} min`,
  /** `when` is "Sat 8 Nov, 18:00" in the viewer's zone. */
  endsAt: (when: string) => `Ends ${when}`,
  /** `day` is "Sat 1 Nov". */
  endedOn: (day: string) => `Ended ${day}`,

  voteToSeeResults: 'Vote to see results',
  noAnswersYet: 'No answers yet',
  peopleAnswered: (n: number) => (n === 1 ? '1 person answered' : `${n} people answered`),
  peopleAnsweredSeeWho: (n: number) =>
    `${n === 1 ? '1 person answered' : `${n} people answered`} · See who`,
  somePickedMoreThanOne: 'Some people picked more than one',
  noOneAnswered: 'No one answered',
  countsOnceYoureIn: "Counts once you're in",

  /** Per option once results show: "60% · 6". */
  shareAndCount: (share: number, count: number) => `${share}% · ${count}`,
  /**
   * An option's result as its meter's value; the meter is named by the option's text, so a screen
   * reader says "Cais do Sodré, 6 answers, 60%" (TECH-197 § Edge cases "Accessibility").
   */
  optionResult: (count: number, share: number) =>
    `${count === 1 ? '1 answer' : `${count} answers`}, ${share}%`,
  /** Screen readers only, beside a result: the viewer's own pick (drawn as the filled tick). */
  yourAnswer: 'Your answer',
  /** Screen readers only, on an ended poll: a leading option (drawn as the circle-check). */
  mostAnswers: 'Most answers',

  /** The ⋯ menu's rows on a poll (E1, E2, E13, E19a); Bookmark, Report and Delete are today's words. */
  menuPinPoll: 'Pin poll',
  /** No frame draws a pinned poll's row: "Unpin" as today's post row, with the poll's noun. */
  menuUnpinPoll: 'Unpin poll',
  menuBookmark: 'Bookmark',
  /** E1: before the first answer only (approved). */
  menuEditPoll: 'Edit poll',
  menuEndPollNow: 'End poll now',
  menuReopenPoll: 'Reopen poll',
  menuReport: 'Report',
  menuDelete: 'Delete',
  /** The ⋯ trigger's accessible name on a poll card: today's string (profile and Space headers). */
  moreOptions: 'More options',

  /** E3: the End confirm. */
  endTitle: 'End this poll now?',
  endBody:
    'Everyone sees the final results and no one can answer after this. You can reopen it later.',
  endConfirm: 'End poll',
  cancel: 'Cancel',

  /** B6: the "See who" drawer. */
  whoAnswered: 'Who answered',
  /** A chip: "Cais do Sodré · 6". */
  optionChip: (option: string, count: number) => `${option}${POLL_CHIP_COUNT(count)}`,
  /** The chip's count, kept whole after a long option that truncates. */
  optionChipCount: (count: number) => POLL_CHIP_COUNT(count),
  /** After the viewer's own name in the list: "João S. (you)". */
  you: '(you)',
  /** Today's list words (WeMetRosterDrawer). */
  loadMore: 'Load more',
  loadingMore: 'Loading more',
  loadMoreFailed: "Couldn't load more. Retry",
  /** The list's first page failed (no frame draws it). */
  loadFailed: "Couldn't load who answered. Retry",

  /** E14, E14b, A3, A3b: the duration field and the Reopen drawer. */
  setPollDuration: 'Set poll duration',
  ends: 'Ends',
  pickDateInstead: 'Pick a date and time instead',
  changeDate: 'Change date and time',
  useSliderInstead: 'Use the slider instead',
  /** Next to a resulting time: "Tomorrow, 00:40". "Today" has no frame; the same pattern. */
  today: 'Today',
  tomorrow: 'Tomorrow',
  /** The date and time pickers' names while choosing an end (no frame draws them). */
  pickEndDate: 'End date',
  pickEndTime: 'End time',
  reopenTitle: 'Reopen this poll?',
  reopenBody: 'Answers so far are kept.',
  reopenConfirm: 'Reopen poll',
  /** E15. */
  reopenedToast: 'Your poll is open again',

  /** The generic error toast (CONTRACT § 4.0 "generic"), today's words. */
  somethingWentWrong: 'Something went wrong. Please try again.',

  /** E10: the error toast when an answer did not go through (reverted). */
  answerFailed: "Your answer didn't go through. Try again.",
  /** Said politely when the viewer takes their answer back (TECH-197 § Voting). */
  answerRemoved: 'Answer removed',

  // Signed out (B7, B7b, E5, OPTION 1 · 1c). `{option}` is the pick; several are listed.
  answerSaved: 'Your answer is saved',
  youPickedSignIn: (options: readonly string[]) =>
    `You picked ${pollOptionList(options)}. It will count once you sign in.`,
  /** B7 when another sign-in intent is waiting, so the pick will not be replayed (no promise it counts). */
  youPicked: (options: readonly string[]) => `You picked ${pollOptionList(options)}.`,
  signInToSubmitAnswer: 'Sign in to submit your answer',
  signInToSubmitAnswers: 'Sign in to submit your answers',
  tickAsManyAsYouLike: 'Tick as many as you like. They count once you sign in.',
  /** E5: "Your answer is saved. It will count once you sign in.", "sign in" the link. */
  savedLineBefore: 'Your answer is saved. It will count once you ',
  savedLineLink: 'sign in',
  savedLineAfter: '.',

  /** B8, 1c+: the answer saved signed out is in. */
  answerIn: 'Your answer is in',
  /** B10. */
  answerInJoined: (space: string) => `Your answer is in. You've joined ${space}.`,
  /** B11 (the footer is `countsOnceYoureIn`). */
  joinRequestSent: (space: string) =>
    `Join request sent. Your answer counts once ${space} lets you in.`,
  /** E16: under today's "Got a PIN from the host?". */
  pinLine: (options: readonly string[]) =>
    `You picked ${pollOptionList(options)}. Enter the PIN to join, and your answer goes in.`,
  /** E17, and E6's replay toast. */
  onlyMembersCanAnswer: (space: string) => `Only members of ${space} can answer`,
  /** E17 when the server names no Space (one the viewer may not be told). No frame. */
  onlyMembersCanAnswerUnnamed: 'Only members can answer',
  openSpace: (space: string) => `Open ${space}`,
  /** E6: the replay after sign-in did not cast the answer. */
  pollEndedBeforeSignIn: 'This poll ended before you signed in',
  onlyPeopleGoingCanAnswer: (event: string) => `Only people Going to ${event} can answer`,
  pollChanged: 'This poll changed. Have another look.',
} as const;

/** "A", "A and B", "A, B and C": the picks named in one sentence (B7, E16 name one; DRAFT). */
function pollOptionList(options: readonly string[]): string {
  if (options.length <= 1) return options[0] ?? '';
  return `${options.slice(0, -1).join(', ')} and ${options[options.length - 1]}`;
}

/** The card's top line: "Pick one" / "Pick one or more", "· Anonymous poll" only when names are hidden. */
export const pollTopLine = ({
  allowsMultiple,
  hideVoterNames,
  isClosed,
}: {
  allowsMultiple: boolean;
  hideVoterNames: boolean;
  isClosed: boolean;
}): string => {
  if (isClosed) return POLL_COPY.finalResults;
  const pick = allowsMultiple ? POLL_COPY.pickOneOrMore : POLL_COPY.pickOne;
  return hideVoterNames ? `${pick} · ${POLL_COPY.anonymousPoll}` : pick;
};

/**
 * The Poll composer (`/create-poll`, FE-2b; frames A2, A3, A3b, A4, A5, E7, E8, E21). DRAFT, as the
 * module. Today's composer strings ("Who can see this", "Where to post", "This Space only", "Post",
 * "Posting…", "Cancel", "Save") are reused as the post composer writes them.
 */
export const POLL_COMPOSER_COPY = {
  kindLabel: 'Poll',
  questionPlaceholder: 'Ask your question',
  descriptionPlaceholder: 'Add a little context (optional)',
  optionPlaceholder: (n: number) => `Option ${n}`,
  addOption: 'Add an option',
  /** The remove control's accessible name (no frame draws words for it). */
  removeOption: (n: number) => `Remove option ${n}`,
  allowMultiple: 'Allow multiple answers',
  hideVoterName: 'Hide voter name',
  // E21, inline under the field (P2: the blank one only when a filled field follows it).
  duplicateOption: 'That answer is already there. Change it or remove it.',
  blankOption: 'Add an answer here, or remove it.',

  // Bottom bar.
  whoCanSee: 'Who can see this',
  whereToPost: 'Where to post',
  thisSpaceOnly: 'This Space only',
  anyone: 'Anyone',
  peopleGoing: 'People Going',
  /** "✓ 1 Space", "✓ 2 Spaces", "✓ 1 Event". */
  placesChosen: (spaces: number, events: number) =>
    events > 0 ? '✓ 1 Event' : `✓ ${spaces} ${spaces === 1 ? 'Space' : 'Spaces'}`,
  /** Nothing ticked yet (opened with no place): the post composer's own pill. */
  noPlaceChosen: '+ Spaces',
  onlyPeopleGoing: 'Only people Going will see this poll.',
  /** The composer's first step only opens the place picker (founder, 3 Nov); the picker keeps `post`. */
  next: 'Next',
  post: 'Post',
  posting: 'Posting…',

  // E4: the edit composer (production's edit label for the button).
  placementLocked: "Who sees it and where it's posted can't change now.",
  /**
   * A refused Event (403 `forbidden_event_ids`: over, cancelled, or no longer the author's to post
   * in). No frame: today's "That didn't go through" title with a line for an Event.
   */
  eventRefusedTitle: "That didn't go through",
  eventRefused:
    "This Event isn't taking polls any more. Check where your poll is going and try again.",
  update: 'Update',

  // L0: the Live poll strip, hosts only (TECH-197 builds it; TECH-867 owns live mode).
  livePoll: 'Live poll',
  livePollLine: 'Show it on a big screen. The room answers by QR.',

  // E7: "Who can see and answer this poll?" (VisibilitySelector, poll wording).
  visibilityTitle: 'Who can see and answer this poll?',
  visibilityAnyone: 'Anyone',
  visibilityAnyoneHelper:
    'Shows in the Spaces you picked and in the public feed. Anyone who can see it can answer.',
  visibilitySpaceOnly: 'Only selected Spaces and Events',
  visibilitySpaceOnlyHelper:
    'Only members of the Spaces you picked, or people Going to the Event, can see and answer it.',

  // A4, A5: the picker.
  pickerTitle: 'Where should this poll go?',
  pickerSubtitle: 'Several Spaces, or one Event.',
  pickerSearch: 'Search Spaces and Events…',
  postingFromHere: "You're posting from here",
  /** "1 Space selected", "2 Spaces selected". */
  spacesSelected: (n: number) => (n === 1 ? '1 Space selected' : `${n} Spaces selected`),
} as const;
